import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { DatePickerInput } from '@/components/common/DatePickerInput';
import { MemberSelect } from '@/components/common/MemberSelect';
import { OptionSelect } from '@/components/common/OptionSelect';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { promoItemsQueryOptions } from '@/features/promotion/api/promotion.api';
import { regionalSalesManagersQueryOptions } from '@/features/users/api/users.api';
import { DONATION_ITEM_LOOKUP_PARAMS } from '../constants/marketPromotion.constants';
import { useSaveDonationBudget } from '../hooks/useMpP2Mutations';
import type { DonationBudget } from '../types/marketPromotionP2.types';

interface DonationBudgetFormDialogProps {
  budget: DonationBudget | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormState {
  userId: number | undefined;
  promoItemId: number | undefined;
  budgetAmount: string;
  disburseAmount: string;
  budgetDate: string;
  targetDate: string;
  startDate: string;
  comments: string;
}

const EMPTY: FormState = {
  userId: undefined,
  promoItemId: undefined,
  budgetAmount: '',
  disburseAmount: '',
  budgetDate: '',
  targetDate: '',
  startDate: '',
  comments: '',
};

export function DonationBudgetFormDialog({
  budget,
  open,
  onOpenChange,
}: DonationBudgetFormDialogProps) {
  const [form, setForm] = useState<FormState>(() =>
    budget === null
      ? EMPTY
      : {
          userId: budget.userId ?? undefined,
          promoItemId: budget.promoItemId ?? undefined,
          budgetAmount: String(budget.budgetAmount),
          disburseAmount: String(budget.disburseAmount),
          budgetDate: budget.budgetDate?.slice(0, 10) ?? '',
          targetDate: budget.targetDate?.slice(0, 10) ?? '',
          startDate: budget.startDate?.slice(0, 10) ?? '',
          comments: budget.comments ?? '',
        },
  );
  const { data: items, isLoading: itemsLoading } = useQuery(
    promoItemsQueryOptions(DONATION_ITEM_LOOKUP_PARAMS),
  );

  const save = useSaveDonationBudget(() => onOpenChange(false));

  const patch = (next: Partial<FormState>) => setForm((prev) => ({ ...prev, ...next }));

  const isEdit = budget !== null;
  const ready =
    form.userId !== undefined && form.promoItemId !== undefined && form.budgetAmount.trim() !== '';

  function submit() {
    if (form.userId === undefined || form.promoItemId === undefined) return;

    save.mutate({
      id: budget?.id ?? null,
      payload: {
        userId: form.userId,
        promoItemId: form.promoItemId,
        budgetAmount: Number(form.budgetAmount),
        disburseAmount: form.disburseAmount.trim() === '' ? 0 : Number(form.disburseAmount),
        ...(form.budgetDate === '' ? {} : { budgetDate: form.budgetDate }),
        ...(form.targetDate === '' ? {} : { targetDate: form.targetDate }),
        ...(form.startDate === '' ? {} : { startDate: form.startDate }),
        ...(form.comments.trim() === '' ? {} : { comments: form.comments.trim() }),
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <div className="min-w-0">
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit Budget' : 'Add Budget'}</DialogTitle>
            <DialogDescription>
              {isEdit
                ? 'You can change the holder, the item and the amounts.'
                : 'Give a member a budget against one cash promotional item.'}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="mpbf-member">RSM Name</Label>
              <MemberSelect
                id="mpbf-member"
                membersQuery={regionalSalesManagersQueryOptions()}
                placeholder="Pick an RSM"
                value={form.userId}
                onChange={(userId) => patch({ userId })}
              />
            </div>

            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="mpbf-item">Donation Item</Label>
              <OptionSelect
                id="mpbf-item"
                options={items?.data.map((item) => ({ id: item.id, name: item.name }))}
                isLoading={itemsLoading}
                value={form.promoItemId}
                onChange={(promoItemId) => patch({ promoItemId })}
                placeholder="Pick an item"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mpbf-budget">Budget Amount</Label>
              <Input
                id="mpbf-budget"
                value={form.budgetAmount}
                onChange={(event) => patch({ budgetAmount: event.target.value })}
                inputMode="numeric"
                autoComplete="off"
                className="tabular-nums"
                placeholder="500000"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="mpbf-disburse">Disburse Amount</Label>
              <Input
                id="mpbf-disburse"
                value={form.disburseAmount}
                onChange={(event) => patch({ disburseAmount: event.target.value })}
                inputMode="numeric"
                autoComplete="off"
                className="tabular-nums"
                placeholder="0"
              />
            </div>

            {isEdit ? null : (
              <>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="mpbf-budget-date">Budget Date</Label>
                  <DatePickerInput
                    id="mpbf-budget-date"
                    value={form.budgetDate}
                    onChange={(budgetDate) => patch({ budgetDate })}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="mpbf-target-date">Target Date</Label>
                  <DatePickerInput
                    id="mpbf-target-date"
                    value={form.targetDate}
                    onChange={(targetDate) => patch({ targetDate })}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="mpbf-start-date">Start Date</Label>
                  <DatePickerInput
                    id="mpbf-start-date"
                    value={form.startDate}
                    onChange={(startDate) => patch({ startDate })}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="mpbf-comments">Comments</Label>
                  <Input
                    id="mpbf-comments"
                    value={form.comments}
                    onChange={(event) => patch({ comments: event.target.value })}
                    placeholder="Optional"
                    autoComplete="off"
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={submit} disabled={save.isPending || !ready}>
              {save.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
